import React, {useCallback, useState, useRef, useImperativeHandle, useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';

// @mui
import {styled} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';

// Accessibility
import {useAnnounce} from 'packages/common/hooks/useAccessibility';

/**
 * @file Grille de sélection de créneaux horaires accessible (pattern listbox).
 * @module packages.common.components.table-reservation.components.TimeSlotPicker
 */

// ----------------------------------------------------------------------

const TimeSlotButton = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'compact' && prop !== 'isWaitlist',
})(({theme, isSelected, compact, isWaitlist}) => ({
  'width': '100%',
  'height': isWaitlist ? (compact ? 46 : 50) : (compact ? 40 : 44),
  'borderRadius': 8,
  'display': 'flex',
  'flexDirection': 'column',
  'alignItems': 'center',
  'justifyContent': 'center',
  'cursor': 'pointer',
  'fontWeight': 500,
  'fontSize': compact ? '0.85rem' : '0.9rem',
  'position': 'relative',

  // Styles par défaut (créneaux normaux)
  ...(!isWaitlist && {
    'border': `1px solid ${isSelected ? theme.palette.grey[800] : theme.palette.grey[300]}`,
    'backgroundColor': isSelected ? theme.palette.grey[800] : theme.palette.grey[50],
    'color': isSelected ? theme.palette.common.white : theme.palette.grey[700],
  }),

  // Styles pour créneaux waitlist (orange doré)
  ...(isWaitlist && {
    'border': `1px solid ${isSelected ? theme.palette.orange.main : theme.palette.orange.light}`,
    'backgroundColor': isSelected ? theme.palette.orange.main : theme.palette.orange.surface,
    'color': isSelected ? theme.palette.orange.contrastText : theme.palette.orange.main,
  }),

  'boxShadow': isSelected ?
    '0 1px 4px rgba(0, 0, 0, 0.1)' :
    '0 1px 2px rgba(0, 0, 0, 0.05)',
  'transition': theme.transitions.create(['all'], {
    duration: 200,
    easing: theme.transitions.easing.easeOut,
  }),

  '&:hover': {
    'transform': 'translateY(-1px)',
    'boxShadow': isSelected ?
      '0 2px 6px rgba(0, 0, 0, 0.12)' :
      '0 1px 4px rgba(0, 0, 0, 0.08)',
    ...(!isWaitlist && {
      borderColor: isSelected ? theme.palette.grey[700] : theme.palette.grey[400],
      backgroundColor: isSelected ? theme.palette.grey[700] : theme.palette.grey[100],
    }),
    ...(isWaitlist && {
      borderColor: theme.palette.orange.main,
      backgroundColor: isSelected ? theme.palette.orange.light : theme.palette.orange.lighter,
    }),
  },

  '&:active': {
    transform: 'translateY(0)',
  },

  // Styles de focus visible pour l'accessibilité clavier
  '&:focus': {
    outline: 'none',
  },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: isWaitlist ? theme.palette.orange.main : theme.palette.grey[800],
    outlineOffset: '2px',
  },
}));

const WaitlistBadge = styled(Box)(({theme}) => ({
  fontSize: theme.typography.micro.fontSize,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.3px',
  marginTop: 1,
  color: 'inherit',
  opacity: 0.9,
  whiteSpace: 'nowrap',
}));

// ----------------------------------------------------------------------

/**
 * Affiche les créneaux horaires triés chronologiquement sous forme de grille
 * sélectionnable. Distingue visuellement les créneaux en liste d'attente,
 * implémente la navigation clavier ARIA listbox et expose une méthode `focus`
 * via ref pour permettre au parent de cibler le créneau actif.
 *
 * @param {Object} props - Props du composant.
 * @param {Array<Object>|Object} props.timeSlots - Créneaux, sous forme de tableau ou d'objet contenant `timeslots`.
 * @param {string} [props.selectedTime] - Heure du créneau sélectionné ('HH:mm').
 * @param {Function} props.onTimeSelect - Callback (time, slot) à la sélection d'un créneau.
 * @param {boolean} [props.compact=false] - Affichage compact (boutons réduits).
 * @param {boolean} [props.showGroupHeaders=false] - Affiche les en-têtes de groupe de créneaux.
 * @param {React.Ref} ref - Ref exposant une méthode `focus()`.
 * @return {JSX.Element} Grille de créneaux ou message d'indisponibilité.
 */
export const TimeSlotPicker = React.forwardRef(function TimeSlotPicker({
  timeSlots,
  selectedTime,
  onTimeSelect,
  compact = false,
  showGroupHeaders = false, // Par défaut, ne pas montrer les en-têtes de groupe
}, ref) {
  // Ref pour le conteneur listbox
  const containerRef = useRef(null);

  // S'assurer que timeSlots est un tableau
  const safeTimeSlots = useMemo(() => {
    if (!timeSlots) return [];
    if (timeSlots.timeslots && Array.isArray(timeSlots.timeslots)) {
      return timeSlots.timeslots;
    }
    if (Array.isArray(timeSlots)) {
      return timeSlots;
    }
    return [];
  }, [timeSlots]);

  // Trier les créneaux horaires par ordre chronologique (timestamp)
  const sortedTimeSlots = useMemo(() => {
    return [...safeTimeSlots].sort((a, b) => {
      if (!a || !b) return 0;

      // Priorité au timestamp si disponible (ordre chronologique exact)
      if (a.timestamp && b.timestamp) {
        return a.timestamp - b.timestamp;
      }

      // Fallback sur le tri par heure string si pas de timestamp
      if (a.time && b.time) {
        return a.time.localeCompare(b.time);
      }

      return 0;
    });
  }, [safeTimeSlots]);

  // Regroupement des créneaux par rotation (« 1er service », « 2e service »...)
  // lorsque l'établissement scinde le service en plusieurs rotations. Chaque créneau
  // porte alors `rotationIndex`, `rotationLabel` et `occupiedUntil` (heure de libération).
  // On conserve l'index global (position dans `sortedTimeSlots`) pour préserver la
  // navigation clavier de la grille.
  const groupedTimeSlots = useMemo(() => {
    const hasRotations = showGroupHeaders && sortedTimeSlots.some((slot) => slot && slot.rotationLabel);
    if (!hasRotations) return null;

    const byRotation = new Map();
    sortedTimeSlots.forEach((slot, globalIndex) => {
      if (!slot) return;
      const key = slot.rotationIndex ?? slot.rotationLabel ?? 0;
      if (!byRotation.has(key)) {
        byRotation.set(key, {
          key,
          rotationIndex: slot.rotationIndex ?? 0,
          rotationLabel: slot.rotationLabel,
          occupiedUntil: slot.occupiedUntil,
          slots: [],
        });
      }
      byRotation.get(key).slots.push({slot, globalIndex});
    });

    return Array.from(byRotation.values()).sort((a, b) => a.rotationIndex - b.rotationIndex);
  }, [showGroupHeaders, sortedTimeSlots]);

  // Index du créneau actuellement focusé pour la navigation clavier
  const [focusedIndex, setFocusedIndex] = useState(() => {
    if (selectedTime) {
      const idx = sortedTimeSlots.findIndex((s) => s?.time === selectedTime);
      return idx >= 0 ? idx : 0;
    }
    return 0;
  });

  // Mettre à jour focusedIndex quand selectedTime change
  useEffect(() => {
    if (selectedTime) {
      const idx = sortedTimeSlots.findIndex((s) => s?.time === selectedTime);
      if (idx >= 0) {
        setFocusedIndex(idx);
      }
    }
  }, [selectedTime, sortedTimeSlots]);

  // Hook pour annoncer les changements aux lecteurs d'écran
  const announce = useAnnounce();

  // Exposer une méthode focus pour permettre au parent de focus le premier créneau
  useImperativeHandle(ref, () => ({
    focus: () => {
      const element = containerRef.current?.querySelector(`[data-slot-index="${focusedIndex}"]`);
      if (element) {
        element.focus();
      }
    },
  }), [focusedIndex]);

  // Gestionnaire de sélection avec annonce accessible
  const handleSelect = useCallback((time, slot) => {
    const isWaitlist = slot.waitlistAvailable && !slot.available;
    const message = isWaitlist ?
      `Créneau ${time} sélectionné, liste d'attente` :
      `Créneau ${time} sélectionné`;
    announce(message);
    onTimeSelect(time, slot);
  }, [onTimeSelect, announce]);

  // Focus un créneau spécifique par son index
  const focusSlotByIndex = useCallback((index) => {
    setFocusedIndex(index);
    setTimeout(() => {
      const element = containerRef.current?.querySelector(`[data-slot-index="${index}"]`);
      if (element) {
        element.focus();
      }
    }, 0);
  }, []);

  // Gestionnaire clavier pour la navigation dans la listbox
  const handleKeyDown = useCallback((event, index, time, slot) => {
    const totalSlots = sortedTimeSlots.length;
    // Calculer le nombre de colonnes (3 sur mobile, 4 sur desktop)
    const columnsPerRow = 3;

    let newIndex = index;
    let handled = false;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleSelect(time, slot);
        return;
      case 'ArrowRight':
        newIndex = Math.min(index + 1, totalSlots - 1);
        handled = true;
        break;
      case 'ArrowLeft':
        newIndex = Math.max(index - 1, 0);
        handled = true;
        break;
      case 'ArrowDown':
        newIndex = Math.min(index + columnsPerRow, totalSlots - 1);
        handled = true;
        break;
      case 'ArrowUp':
        newIndex = Math.max(index - columnsPerRow, 0);
        handled = true;
        break;
      case 'Home':
        newIndex = 0;
        handled = true;
        break;
      case 'End':
        newIndex = totalSlots - 1;
        handled = true;
        break;
      default:
        return;
    }

    if (handled && newIndex !== index) {
      event.preventDefault();
      event.stopPropagation();
      focusSlotByIndex(newIndex);
      // Annoncer le nouveau créneau pour les lecteurs d'écran
      const newSlot = sortedTimeSlots[newIndex];
      if (newSlot) {
        const isWaitlist = newSlot.waitlistAvailable && !newSlot.available;
        announce(isWaitlist ? `${newSlot.time}, liste d'attente` : newSlot.time);
      }
    }
  }, [sortedTimeSlots, handleSelect, focusSlotByIndex, announce]);

  // Si aucun créneau disponible
  if (sortedTimeSlots.length === 0) {
    return (
      <Alert
        severity="info"
        role="status"
        sx={{mb: 2, fontSize: compact ? '0.75rem' : '0.85rem'}}
      >
        Aucun créneau disponible pour cette date. Veuillez sélectionner une autre date ou contacter directement le restaurant.
      </Alert>
    );
  }

  // Rendu d'un créneau individuel. `index` est l'index global dans
  // `sortedTimeSlots` (préservé même en affichage groupé) pour cohérence du focus.
  const renderSlotItem = (slot, index) => {
    if (!slot || typeof slot.time !== 'string') return null;

    const isSelected = selectedTime === slot.time;
    const isWaitlistSlot = slot.waitlistAvailable && !slot.available;
    const isFocusable = index === focusedIndex;

    const slotLabel = isWaitlistSlot ?
      `${slot.time}, liste d'attente` :
      slot.time;

    return (
      <Grid
        item
        xs={4}
        sm={3}
        md={3}
        key={slot.time}
        sx={{
          'animation': `fadeInUp 0.4s ease-out ${index * 0.05}s both`,
          '@keyframes fadeInUp': {
            '0%': {
              opacity: 0,
              transform: 'translateY(10px)',
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)',
            },
          },
        }}
      >
        <TimeSlotButton
          role="option"
          tabIndex={isFocusable ? 0 : -1}
          aria-selected={isSelected}
          aria-label={slotLabel}
          onClick={() => handleSelect(slot.time, slot)}
          onKeyDown={(e) => handleKeyDown(e, index, slot.time, slot)}
          isSelected={isSelected}
          isWaitlist={isWaitlistSlot}
          compact={compact}
          data-slot-index={index}
        >
          <span aria-hidden="true">{slot.time}</span>
          {isWaitlistSlot && (
            <WaitlistBadge aria-hidden="true">
              Liste d&apos;attente
            </WaitlistBadge>
          )}
        </TimeSlotButton>
      </Grid>
    );
  };

  // Affichage groupé par rotation : chaque rotation (« 1er service »...) porte un
  // en-tête rappelant son nom et l'heure à laquelle la table doit être libérée.
  if (groupedTimeSlots) {
    return (
      <Box
        ref={containerRef}
        sx={{mb: compact ? 1 : 2}}
        role="listbox"
        aria-label="Créneaux horaires disponibles par service"
      >
        {groupedTimeSlots.map((group) => (
          <Box key={group.key} sx={{mb: compact ? 1.5 : 2}}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1,
                px: 0.5,
              }}
            >
              <Typography variant="subtitle2" sx={{fontWeight: 700}}>
                {group.rotationLabel}
              </Typography>
              {group.occupiedUntil && (
                <Typography variant="caption" color="text.secondary" sx={{textAlign: 'right'}}>
                  Table à libérer pour {group.occupiedUntil}
                </Typography>
              )}
            </Box>
            <Grid container spacing={compact ? 1 : 1.5} role="presentation">
              {group.slots.map(({slot, globalIndex}) => renderSlotItem(slot, globalIndex))}
            </Grid>
          </Box>
        ))}
      </Box>
    );
  }

  // Affichage moderne des créneaux avec animation stagger et accessibilité
  return (
    <Box
      ref={containerRef}
      sx={{mb: compact ? 1 : 2}}
      role="listbox"
      aria-label="Créneaux horaires disponibles"
    >
      <Grid container spacing={compact ? 1 : 1.5} role="presentation">
        {sortedTimeSlots.map((slot, index) => renderSlotItem(slot, index))}
      </Grid>
    </Box>
  );
});

TimeSlotPicker.propTypes = {
  timeSlots: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
  ]),
  selectedTime: PropTypes.string,
  onTimeSelect: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  showGroupHeaders: PropTypes.bool,
};
